import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, User, Wallet, Shield, Bell, Info } from 'lucide-react';
import Navigation from '@/components/Navigation';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CryptoSettingsPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [wallets, setWallets] = useState([]);
  const [username, setUsername] = useState('');

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      const token = localStorage.getItem('routeflow_token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      // Load wallets
      const walletsRes = await axios.get(`${API}/crypto/wallet/${user.id}`, config);
      setWallets(walletsRes.data);

      // Try to get username
      try {
        const usernameRes = await axios.get(`${API}/crypto/username/lookup/${user.email}`, config);
        if (usernameRes.data?.username) {
          setUsername(usernameRes.data.username);
        }
      } catch (err) {
        console.log('No username set');
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      toast.error('Failed to load user data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoutConfirm = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      onLogout();
      navigate('/');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
        <Navigation user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      <Navigation user={user} onLogout={onLogout} />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-24">
        <Button
          variant="ghost"
          onClick={() => navigate('/crypto/wallet')}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Wallet
        </Button>

        <h1 className="text-3xl font-bold text-gray-900 mb-2">Settings</h1>
        <p className="text-gray-600 mb-8">Manage your account and preferences</p>

        {/* Profile Information */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center space-x-2">
              <User className="w-5 h-5 text-gray-600" />
              <CardTitle>Profile Information</CardTitle>
            </div>
            <CardDescription>Your account details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Full Name</Label>
              <Input value={user.full_name} disabled className="mt-1" />
            </div>
            <div>
              <Label>Email</Label>
              <Input value={user.email} disabled className="mt-1" />
            </div>
            <div>
              <Label>RouteFlow Username</Label>
              {username ? (
                <div className="mt-1 flex items-center space-x-2">
                  <Input value={`@${username}`} disabled />
                  <Badge variant="success">Active</Badge>
                </div>
              ) : (
                <div className="mt-1">
                  <Alert>
                    <Info className="h-4 w-4" />
                    <AlertDescription>
                      You haven't set up a username yet.{' '}
                      <button
                        onClick={() => navigate('/crypto/setup-username')}
                        className="underline font-medium text-emerald-600 hover:text-emerald-700"
                      >
                        Set it up now
                      </button>
                    </AlertDescription>
                  </Alert>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Wallet Information */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center space-x-2">
              <Wallet className="w-5 h-5 text-gray-600" />
              <CardTitle>Wallets</CardTitle>
            </div>
            <CardDescription>Your connected crypto wallets</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {wallets.map((wallet, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <Badge variant="outline" className="capitalize">
                        {wallet.network.replace('_', ' ')}
                      </Badge>
                      <Badge variant="success" className="text-xs">Active</Badge>
                    </div>
                  </div>
                  <p className="text-sm font-mono text-gray-700">
                    {wallet.address}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Created: {new Date(wallet.created_at).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Security */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center space-x-2">
              <Shield className="w-5 h-5 text-gray-600" />
              <CardTitle>Security</CardTitle>
            </div>
            <CardDescription>Manage your security settings</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Your private keys are encrypted and stored securely. We never have access to your funds.
              </AlertDescription>
            </Alert>
            <div className="pt-2">
              <Button variant="outline" disabled className="w-full sm:w-auto">
                Change Password (Coming Soon)
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center space-x-2">
              <Bell className="w-5 h-5 text-gray-600" />
              <CardTitle>Notifications</CardTitle>
            </div>
            <CardDescription>Manage your notification preferences</CardDescription>
          </CardHeader>
          <CardContent>
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Email notifications will be available soon. We'll notify you about incoming transfers and important account updates.
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>

        {/* Account Actions */}
        <Card className="mb-6 border-red-200">
          <CardHeader>
            <CardTitle className="text-red-600">Account Actions</CardTitle>
            <CardDescription>Manage your account</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button
              onClick={handleLogoutConfirm}
              variant="destructive"
              className="w-full sm:w-auto"
            >
              Logout
            </Button>
          </CardContent>
        </Card>

        {/* App Information */}
        <Card>
          <CardHeader>
            <CardTitle>About RouteFlow AI</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-gray-600">
            <p>Version: 1.0.0</p>
            <p>Network: Polygon Mainnet</p>
            <p className="pt-2">
              RouteFlow AI makes crypto transfers as easy as sending a text message. 
              No seed phrases, no complex addresses - just simple @username transfers.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CryptoSettingsPage;
